import { useRef, useState } from 'react'
import type { ClipboardEvent, DragEvent } from 'react'
import type { RcFile } from 'antd/es/upload'

// Shared drag-and-drop AND clipboard-paste wiring for every outbound-attachment
// surface — the operator reply composer and new-conversation modal, and the
// customer bubble's composer and new-conversation form — so dropping files and
// pasting screenshots behave identically everywhere. The native File objects
// are normalised to RcFile and pushed through the same `onSelect` picker the
// click-to-attach button uses, so size/type/count validation is applied in one
// place.
interface UseAttachmentDropzoneArgs {
  // The shared picker (useOutboundAttachments().onSelect / a composer's
  // onSelectAttachments) that validates and stages the files.
  onSelect: (file: RcFile, batch: RcFile[]) => void
  // When true, drops are ignored (e.g. while a message is being sent).
  disabled?: boolean
}

export interface AttachmentDropzone {
  isDraggingOver: boolean
  // Spread onto the element that should accept dropped and pasted files. The
  // paste handler sits on the container rather than the textarea because the
  // event bubbles, so one binding covers every field inside the composer.
  dropzoneProps: {
    onDragEnter: (event: DragEvent<HTMLElement>) => void
    onDragLeave: (event: DragEvent<HTMLElement>) => void
    onDragOver: (event: DragEvent<HTMLElement>) => void
    onDrop: (event: DragEvent<HTMLElement>) => void
    onPaste: (event: ClipboardEvent<HTMLElement>) => void
  }
}

// Screenshot pastes arrive with a generic name — Chrome and Safari both hand
// over "image.png" — so every pasted shot in a thread would be called the same
// thing, in the attachment chips and in what the customer receives. A real
// filename copied from the file manager comes through intact and is kept.
const GENERIC_PASTE_NAME = /^(image|grafik|bild)\.(png|jpe?g|gif|webp|bmp)$/i

// `screenshot-2026-08-04-13-45-12.png`, in the operator's local time so it lines
// up with when they actually took the shot. Not translated: this is a filename
// that travels with the message, not UI copy.
export function pastedFileName(file: File, index: number, at: Date): string {
  if (file.name && !GENERIC_PASTE_NAME.test(file.name)) {
    return file.name
  }
  const extension = /\.([a-z0-9]+)$/i.exec(file.name)?.[1] ?? 'png'
  const stamp = [
    at.getFullYear(),
    `${at.getMonth() + 1}`.padStart(2, '0'),
    `${at.getDate()}`.padStart(2, '0'),
    `${at.getHours()}`.padStart(2, '0'),
    `${at.getMinutes()}`.padStart(2, '0'),
    `${at.getSeconds()}`.padStart(2, '0'),
  ].join('-')
  // Several images in one paste would otherwise collide on the same second.
  const suffix = index > 0 ? `-${index + 1}` : ''
  return `screenshot-${stamp}${suffix}.${extension.toLowerCase()}`
}

// The files on a clipboard, if any. `items` is the reliable source across
// browsers — Safari populates it for screenshot pastes where `files` can be
// empty — and `getAsFile` returns null for the text/plain and text/html entries
// that ride along with a rich copy.
export function clipboardFiles(clipboardData: DataTransfer | null): File[] {
  if (!clipboardData) {
    return []
  }
  const fromItems = Array.from(clipboardData.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null)
  return fromItems.length > 0
    ? fromItems
    : Array.from(clipboardData.files ?? [])
}

export function useAttachmentDropzone({
  onSelect,
  disabled = false,
}: UseAttachmentDropzoneArgs): AttachmentDropzone {
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  // Counts nested dragenter/dragleave pairs so the overlay doesn't flicker as
  // the pointer crosses child elements — only the outermost leave clears it.
  const dragCounterRef = useRef(0)

  function handleDragEnter(event: DragEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current += 1
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setIsDraggingOver(true)
    }
  }

  function handleDragLeave(event: DragEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setIsDraggingOver(false)
    }
  }

  function handleDragOver(event: DragEvent<HTMLElement>) {
    // Without preventDefault the browser treats the composer as a non-drop
    // target and navigates to the file instead of firing our onDrop.
    event.preventDefault()
    event.stopPropagation()
  }

  // Normalise native File objects to RcFile with a stable, unique uid so the
  // shared picker and the attachment chip list can key on them, then feed the
  // whole batch through onSelect (once per file) so the picker's size/type/count
  // validation runs exactly as it does for clicked uploads. Date.now is fine
  // here — the index disambiguates files staged in the same tick.
  function stage(
    files: File[],
    rename?: (file: File, index: number) => string
  ) {
    const baseTimestamp = Date.now()
    const rcFiles: RcFile[] = files.map((file, index) => {
      const named = rename
        ? // A File's name is read-only, so a renamed one has to be rebuilt.
          new File([file], rename(file, index), {
            type: file.type,
            lastModified: file.lastModified,
          })
        : file
      const rcFile = named as RcFile
      rcFile.uid = `rc-upload-${baseTimestamp}-${index}`
      return rcFile
    })
    rcFiles.forEach((file) => {
      onSelect(file, rcFiles)
    })
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setIsDraggingOver(false)
    dragCounterRef.current = 0

    if (disabled) {
      return
    }

    const { files } = event.dataTransfer
    if (!files || files.length === 0) {
      return
    }

    stage(Array.from(files))
  }

  // Attach anything file-shaped on the clipboard, so a screenshot goes straight
  // into the message instead of having to be saved and re-uploaded.
  //
  // A plain text paste is left completely alone. On a mixed clipboard (rich
  // content copied from a browser or mail client carries the image AND its
  // surrounding text) both happen: the image is attached and the text still
  // pastes, so nothing the operator copied is silently dropped. preventDefault
  // therefore only fires when there is no text to insert anyway.
  function handlePaste(event: ClipboardEvent<HTMLElement>) {
    if (disabled) {
      return
    }

    const files = clipboardFiles(event.clipboardData)
    if (files.length === 0) {
      return
    }

    const pastedAt = new Date()
    if (!event.clipboardData.getData('text/plain')) {
      event.preventDefault()
    }
    stage(files, (file, index) => pastedFileName(file, index, pastedAt))
  }

  return {
    isDraggingOver,
    dropzoneProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
      onPaste: handlePaste,
    },
  }
}

// The dashed-outline highlight applied to a dropzone container while a drag is
// active. Spread into the container's style behind `isDraggingOver`. Lives here
// (not in AttachmentDropOverlay.tsx) so that file only exports a component,
// keeping React Fast Refresh happy.
export function dropzoneHighlightStyle(token: {
  colorPrimaryBg: string
  colorPrimary: string
  borderRadiusLG: number
}) {
  return {
    backgroundColor: token.colorPrimaryBg,
    borderRadius: token.borderRadiusLG,
    outline: `2px dashed ${token.colorPrimary}`,
    outlineOffset: -2,
  }
}
